"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  deleteFeedItemFromFeed,
  getStoryShareDraft,
  trackFeedShare,
} from "@/actions/feed-share";
import type { FeedShareEntity, FeedShareTarget } from "@/db/schema/feed-share";
import type { StoryShareEntity } from "@/db/schema/story";
import { feedKeys } from "@/features/feed/queries/keys";

type TrackFeedSharePayload = {
  entityType: FeedShareEntity;
  entityId: number;
  target?: FeedShareTarget;
  quoteContent?: string;
};

type DeleteFeedItemPayload = {
  entityType: FeedShareEntity;
  entityId: number;
};

export function useTrackFeedShareMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["feed-share", "track"],
    mutationFn: async (payload: TrackFeedSharePayload) => {
      const result = await trackFeedShare(payload);
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to track share");
      }
      return { ...result.data, payload };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: feedKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["posts"] });
      void queryClient.invalidateQueries({ queryKey: ["activityPosts"] });
      void queryClient.invalidateQueries({ queryKey: ["activitySessions"] });
      void queryClient.invalidateQueries({ queryKey: ["socialFeed"] });
    },
  });
}

export function useStoryShareDraftMutation() {
  return useMutation({
    mutationKey: ["feed-share", "story-draft"],
    mutationFn: async (payload: {
      entityType: StoryShareEntity;
      // Numeric-id entities pass String(id); profile passes the user id.
      entityId: string;
    }) => {
      const result = await getStoryShareDraft(payload);
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to prepare story");
      }
      return result.data;
    },
  });
}

export function useDeleteFeedItemMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["feed-share", "delete-feed-item"],
    mutationFn: async (payload: DeleteFeedItemPayload) => {
      const result = await deleteFeedItemFromFeed(payload);
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to delete feed item");
      }
      return { ...result.data, payload };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: feedKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["posts"] });
      void queryClient.invalidateQueries({ queryKey: ["activityPosts"] });
      void queryClient.invalidateQueries({ queryKey: ["activitySessions"] });
      void queryClient.invalidateQueries({ queryKey: ["socialFeed"] });
    },
  });
}
